import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import {
  adminService,
  type AdminSponsor,
  type SponsorWrite,
} from '../../services/adminService'
import { EmptyState, LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'
import AssetUploadButton from '../AssetUploadButton'

export default function SponsorsManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [sponsors, setSponsors] = useState<AdminSponsor[]>([])
  const [loading, setLoading] = useState(true)
  const [dialog, setDialog] = useState<{ sponsor: AdminSponsor | null } | null>(null)

  const load = () =>
    adminService
      .listSponsors(eventId)
      .then(setSponsors)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const remove = async (s: AdminSponsor) => {
    if (!(await confirmDelete(`Delete sponsor "${s.name}"?`))) return
    try {
      await adminService.deleteSponsor(eventId, s.id)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const exportLeads = async () => {
    try {
      const blob = await adminService.exportSponsorLeads(eventId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'sponsor-leads.csv'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Add sponsors/exhibitors. Show them on the site with a Sponsors block.
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={exportLeads}>
            Export leads
          </Button>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDialog({ sponsor: null })}>
            Add sponsor
          </Button>
        </Stack>
      </Stack>

      {sponsors.length === 0 ? (
        <EmptyState title="No sponsors yet" description="Add your first sponsor or exhibitor." />
      ) : (
        <Stack spacing={1.5}>
          {sponsors.map((s) => (
            <Card key={s.id} variant="outlined" sx={{ p: 2, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              {s.logoUrl ? (
                <Box component="img" src={s.logoUrl} alt={s.name} sx={{ width: 64, height: 40, objectFit: 'contain' }} />
              ) : (
                <Box sx={{ width: 64, height: 40, bgcolor: 'action.hover', borderRadius: 1 }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {s.name}
                  </Typography>
                  {s.tier && <Chip size="small" label={s.tier} sx={{ height: 20, fontWeight: 700 }} />}
                  {s.booth && <Chip size="small" variant="outlined" label={`Booth ${s.booth}`} sx={{ height: 20 }} />}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {s.leadCount ?? 0} interested
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setDialog({ sponsor: s })}>
                <EditRoundedIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => remove(s)}>
                <DeleteRoundedIcon fontSize="small" />
              </IconButton>
            </Card>
          ))}
        </Stack>
      )}

      {dialog && (
        <SponsorDialog
          eventId={eventId}
          sponsor={dialog.sponsor}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null)
            load()
          }}
        />
      )}
    </Box>
  )
}

function SponsorDialog({
  eventId,
  sponsor,
  onClose,
  onSaved,
}: {
  eventId: string
  sponsor: AdminSponsor | null
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: sponsor?.name ?? '',
    tier: sponsor?.tier ?? '',
    tierOrdinal: sponsor?.tierOrdinal ?? 0,
    logoUrl: sponsor?.logoUrl ?? '',
    websiteUrl: sponsor?.websiteUrl ?? '',
    description: sponsor?.description ?? '',
    booth: sponsor?.booth ?? '',
  })
  const set = (k: keyof typeof form, v: string | number) => setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.name.trim()) {
      notify('Name is required', 'error')
      return
    }
    const payload: SponsorWrite = {
      name: form.name.trim(),
      tier: form.tier || null,
      tierOrdinal: Number(form.tierOrdinal) || 0,
      logoUrl: form.logoUrl || null,
      websiteUrl: form.websiteUrl || null,
      description: form.description || null,
      booth: form.booth || null,
    }
    setSaving(true)
    try {
      if (sponsor) await adminService.updateSponsor(eventId, sponsor.id, payload)
      else await adminService.createSponsor(eventId, payload)
      notify(sponsor ? 'Sponsor updated' : 'Sponsor added')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{sponsor ? 'Edit sponsor' : 'Add sponsor'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" required fullWidth value={form.name} onChange={(e) => set('name', e.target.value)} />
          <Stack direction="row" spacing={2}>
            <TextField label="Tier (e.g. Gold)" fullWidth value={form.tier} onChange={(e) => set('tier', e.target.value)} />
            <TextField
              label="Tier order"
              type="number"
              sx={{ width: 140 }}
              value={form.tierOrdinal}
              onChange={(e) => set('tierOrdinal', Number(e.target.value))}
              helperText="0 = first"
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField label="Logo URL" fullWidth value={form.logoUrl} onChange={(e) => set('logoUrl', e.target.value)} />
            <AssetUploadButton eventId={eventId} onUploaded={(url) => set('logoUrl', url)} />
          </Stack>
          <TextField label="Website URL" fullWidth value={form.websiteUrl} onChange={(e) => set('websiteUrl', e.target.value)} />
          <TextField label="Booth" fullWidth value={form.booth} onChange={(e) => set('booth', e.target.value)} />
          <TextField label="Description" fullWidth multiline minRows={2} value={form.description} onChange={(e) => set('description', e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
